import { FC, useRef, useState } from 'react';
import Modal from '../../components/shared/Modal/Modal';
import BtChecklistSelect from '../bt-checklist-select/bt-checklist-select';
import { ChecklistOption } from '../bt-checklist-select/checklist';
import Tabs from '../../components/shared/Tabs';
import { ServiceSupplier } from '../../services/service-supplier';
import { serviceAreas } from '../../services/service-areas';
import { NotificationManager } from 'react-notifications';
import { useTranslation } from 'react-i18next';
import appState from '../../state/AppStateContainer';
import { alreadyUpToDateIdsOf, skippedIdsOf, SupplierBulkRunOutcome } from '../../util/supplierBulkOutcome';

type Area = { _id: string; name: string };

type Props = {
  show: boolean;
  setShow: (value: boolean) => void;
  areas: ChecklistOption[];
  supplierIds: string[];
  onSave: (outcome: SupplierBulkRunOutcome) => void;
  fetchAreas: () => Promise<void>;
  formatSupplierNames?: (ids: string[]) => string | null;
};

const BulkManageCategoriesModal: FC<Props> = ({ show, setShow, areas, supplierIds, onSave, fetchAreas, formatSupplierNames }) => {
  const [t] = useTranslation('supplierDatabase', { keyPrefix: 'bulkManageCategories' });
  // Creating a brand-new org-wide category (/areas/add) is admin-only; write-access
  // users (Inköp/Deltagare) can assign and remove existing categories.
  const canCreateAreas = appState.isAdminRole();
  const [selectedAreaIds, setSelectedAreaIds] = useState<string[]>([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  // Bumped on every clear, so a create that finishes afterwards adds nothing.
  const picksCleared = useRef(0);

  const clearPicks = () => {
    picksCleared.current += 1;
    setSelectedAreaIds([]);
  };

  const handleTabChange = (newTab: number) => {
    setTab(newTab);
    clearPicks();
  };

  const handleCreate = async (name: string) => {
    const clearedAtStart = picksCleared.current;
    try {
      const area: Area | undefined = await serviceAreas.addOrFindArea(name);
      await fetchAreas();
      if (!area || picksCleared.current !== clearedAtStart) return;
      setSelectedAreaIds(current =>
        current.includes(area._id) ? current : [...current, area._id],
      );
    } catch {
      NotificationManager.error(
        t('notification.createError.description', 'Kunde inte skapa kategorin'),
        t('notification.createError.title', 'Något gick fel')
      );
    }
  };

  const handleSave = async () => {
    if (!selectedAreaIds.length) return;
    setLoading(true);
    try {
      const serviceCall = tab === 0 ? ServiceSupplier.bulkAddAreas : ServiceSupplier.bulkRemoveAreas;
      const result = await serviceCall(supplierIds, selectedAreaIds);
      const changed = result?.updated ?? 0;
      const alreadyUpToDateIds = alreadyUpToDateIdsOf(result);
      const skippedIds = skippedIdsOf(result);
      if (changed > 0) {
        NotificationManager.success(
          t('notification.successCount', {
            count: changed,
            defaultValue_one: 'Kategorierna har uppdaterats för {{count}} leverantör',
            defaultValue_other: 'Kategorierna har uppdaterats för {{count}} leverantörer',
          }),
          t('notification.success.title', 'Sparat')
        );
      } else if (alreadyUpToDateIds.length === 0 && skippedIds.length === 0) {
        // Pre-contract server: no buckets to report — keep the old fixed toast rather
        // than a counted one asserting "för 0 leverantörer".
        NotificationManager.success(
          t('notification.success.description', 'Kategorierna har uppdaterats'),
          t('notification.success.title', 'Sparat')
        );
      }
      if (alreadyUpToDateIds.length > 0) {
        const names = formatSupplierNames?.(alreadyUpToDateIds) ?? null;
        const alreadyText = tab === 0
          ? (names
            ? t('notification.alreadyAdd.withNames', {
              count: alreadyUpToDateIds.length,
              names,
              defaultValue_one: '{{count}} leverantör hade redan kategorierna: {{names}}',
              defaultValue_other: '{{count}} leverantörer hade redan kategorierna: {{names}}',
            })
            : t('notification.alreadyAdd.countOnly', {
              count: alreadyUpToDateIds.length,
              defaultValue_one: '{{count}} leverantör hade redan kategorierna',
              defaultValue_other: '{{count}} leverantörer hade redan kategorierna',
            }))
          : (names
            ? t('notification.alreadyRemove.withNames', {
              count: alreadyUpToDateIds.length,
              names,
              defaultValue_one: '{{count}} leverantör hade inte kategorierna: {{names}}',
              defaultValue_other: '{{count}} leverantörer hade inte kategorierna: {{names}}',
            })
            : t('notification.alreadyRemove.countOnly', {
              count: alreadyUpToDateIds.length,
              defaultValue_one: '{{count}} leverantör hade inte kategorierna',
              defaultValue_other: '{{count}} leverantörer hade inte kategorierna',
            }));
        NotificationManager.info(alreadyText);
      }
      if (skippedIds.length > 0) {
        const names = formatSupplierNames?.(skippedIds) ?? null;
        NotificationManager.info(names
          ? t('notification.skipped.withNames', {
            count: skippedIds.length,
            names,
            defaultValue_one: '{{count}} leverantör kunde inte uppdateras (borttagen eller otillgänglig): {{names}}',
            defaultValue_other: '{{count}} leverantörer kunde inte uppdateras (borttagna eller otillgängliga): {{names}}',
          })
          : t('notification.skipped.countOnly', {
            count: skippedIds.length,
            defaultValue_one: '{{count}} leverantör kunde inte uppdateras (borttagen eller otillgänglig)',
            defaultValue_other: '{{count}} leverantörer kunde inte uppdateras (borttagna eller otillgängliga)',
          }));
      }
      onSave({ changed, skippedIds });
      setShow(false);
      clearPicks();
    } catch (e) {
      const errorKey = (e as any)?.response?.data?.error;
      const errorMsg = errorKey === 'some.areas.not.found'
        ? t('notification.error.areasNotFound', 'Någon av kategorierna finns inte längre. Ladda om sidan och försök igen.')
        : errorKey === 'error.bulkWriteFailed'
          ? t('notification.error.partialWrite', 'Något gick fel och vissa leverantörer kan ha uppdaterats. Ladda om sidan för att se vad som hände.')
          : errorKey ?? t('notification.error.description', 'Kunde inte uppdatera kategorierna');
      NotificationManager.error(errorMsg, t('notification.error.title', 'Något gick fel'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      setShow={(v: boolean) => { if (!v) clearPicks(); setShow(v); }}
      title={t('title', 'Hantera kategorier')}
      buttonInfo={{
        label: t('button.save', 'Spara'),
        action: handleSave,
        loaderShow: loading,
        disabled: selectedAreaIds.length === 0,
      }}
    >
      <div style={{ fontSize: '14px', color: 'var(--gray-700)', width: '500px', display: 'flex', gap: '16px', flexDirection: 'column' }}>
        <Tabs
          tabs={[t('tabs.add', 'Lägg till'), t('tabs.remove', 'Ta bort')]}
          selected={tab}
          onChange={handleTabChange}
        />
        <BtChecklistSelect
          placeholder={t('placeholder.categories', 'Välj kategorier')}
          options={areas}
          value={selectedAreaIds}
          onChange={setSelectedAreaIds}
          onCreate={tab === 0 && canCreateAreas ? handleCreate : undefined}
        />
      </div>
    </Modal>
  );
};

export default BulkManageCategoriesModal;
